import type { WidgetConfig, Message, AgentConfig } from "../../shared/types";
import { Header } from "./Header";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";

interface WidgetDisplayProps {
  widgetConfig: WidgetConfig;
  agentConfig: AgentConfig;
  messages: Message[];
  isLoading: boolean;
  statusMessage: string | null;
  conversationId: string | null;
  onSendMessage: (content: string) => Promise<void>;
  onRetryMessage: (messageId: string) => Promise<void>;
  onClose: () => void;
  widgetRef?: React.RefObject<HTMLDivElement>;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  disableSendMessage?: boolean;
}

export function WidgetDisplay({
  widgetConfig,
  agentConfig,
  messages,
  isLoading,
  statusMessage,
  conversationId,
  onSendMessage,
  onRetryMessage,
  onClose,
  widgetRef,
  textareaRef,
  disableSendMessage = false,
}: WidgetDisplayProps) {
  const primaryColor = agentConfig?.primaryColor || "#CC0020";
  const disclaimer =
    "By using this AI powered Chat, you agree to our [Terms of Use](https://www.civicplus.help/civicplus-help-center/docs/civicpluss-terms-of-use).";

  return (
    <div
      ref={widgetRef}
      className="flex h-full flex-col overflow-hidden rounded-xl border border-white/50 shadow-[0_7px_8px_0_rgba(56,56,56,0.16),0_5px_22px_0_rgba(56,56,56,0.09),0_12px_17px_0_rgba(56,56,56,0.11)]"
      role="region"
      aria-label="Chat widget"
    >
      <Header
        title={agentConfig?.agentTitle || agentConfig?.name}
        subtitle={agentConfig?.subheading || agentConfig?.subtitle}
        logoUrl={agentConfig?.iconUrl || undefined}
        primaryColor={primaryColor}
        onClose={onClose}
      />
      <div className="flex flex-1 flex-col overflow-hidden bg-[#FAFAFA]">
        <MessageList
          messages={messages}
          isLoading={isLoading}
          statusMessage={statusMessage}
          primaryColor={primaryColor}
          conversationId={conversationId}
          sourceUrl={widgetConfig.sourceUrl}
          agentId={widgetConfig.agentId}
          organizationId={widgetConfig.organizationId}
          onRetryMessage={onRetryMessage}
        />
        <MessageInput
          onSendMessage={onSendMessage}
          disabled={isLoading}
          primaryColor={primaryColor}
          textareaRef={textareaRef}
          footerText={
            agentConfig?.footerText
              ? agentConfig.footerText + "\n\n" + disclaimer
              : disclaimer
          }
          disableSendMessage={disableSendMessage}
        />
      </div>
    </div>
  );
}
